import type { BaseServiceOptions, Pagination } from '@/core/types'
import { serviceConfig, logsServiceHandlers, TLog, TLogs } from '../../..'
import { useNotification, useServiceHandlers, defaultPagination } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'

const { model } = serviceConfig.logsProduction

export const useLogsReportQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, filters, modelId, enableQuery, handlers } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TLog>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const loadRecords = useQuery<TLogs, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords, modelId],
    queryFn: () => {
      return logsServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
        modelId,
      })
    },
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
  })
  const addRecord = useMutation<TLog, AxiosError, TLog>(
    (payload: TLog) => logsServiceHandlers.addRecord(payload, { modelId }),
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TLog, AxiosError, TLog>(
    (payload: TLog) =>
      logsServiceHandlers.editRecord(payload, {
        modelId,
        modelChildId: payload.id,
      }),
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecord = useMutation<void, AxiosError, TLog>(
    (payload) =>
      logsServiceHandlers.deleteRecord({
        modelChildId: payload.id,
        modelId,
      }),
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  return {
    addRecord,
    editRecord,
    loadRecords,
    deleteRecord,
  }
}
